import { cssVars } from '@/lib/utils'

/** Una macchia di luce: posizione, diametro, colore e, se la luce scorre, dove va e in quanto tempo. */
interface Glow {
  x: string
  y: string
  size: string
  color: string
  dx?: string
  dy?: string
  t?: number
  delay?: number
}

/**
 * abyss: hero, la luce più viva; current: sezioni scure tranquille; pearl: sezioni chiare;
 * surge: inviti finali, luce concentrata al centro dietro il vetro.
 */
export type LightFieldVariant = 'abyss' | 'current' | 'pearl' | 'surge'

const FIELDS: Record<LightFieldVariant, Glow[]> = {
  abyss: [
    { x: '74%', y: '40%', size: '78vmax', color: 'rgb(0 117 217 / 0.55)', dx: '-7vw', dy: '5vh', t: 26 },
    { x: '92%', y: '86%', size: '46vmax', color: 'rgb(127 216 255 / 0.26)', dx: '-5vw', dy: '-6vh', t: 21, delay: -6 },
    { x: '14%', y: '92%', size: '58vmax', color: 'rgb(84 82 230 / 0.24)', dx: '6vw', dy: '-4vh', t: 30, delay: -12 },
    { x: '6%', y: '4%', size: '52vmax', color: 'rgb(11 47 102 / 0.9)', dx: '4vw', dy: '6vh', t: 34 },
  ],
  current: [
    { x: '86%', y: '18%', size: '62vmax', color: 'rgb(0 117 217 / 0.32)', dx: '-6vw', dy: '6vh', t: 30 },
    { x: '8%', y: '88%', size: '52vmax', color: 'rgb(84 82 230 / 0.18)', dx: '5vw', dy: '-5vh', t: 34, delay: -10 },
    { x: '50%', y: '120%', size: '60vmax', color: 'rgb(127 216 255 / 0.12)', dx: '-4vw', dy: '-4vh', t: 28, delay: -4 },
  ],
  pearl: [
    { x: '88%', y: '6%', size: '58vmax', color: 'rgb(0 117 217 / 0.13)', dx: '-5vw', dy: '5vh', t: 32 },
    { x: '6%', y: '94%', size: '54vmax', color: 'rgb(127 216 255 / 0.32)', dx: '6vw', dy: '-4vh', t: 28, delay: -8 },
    { x: '58%', y: '62%', size: '42vmax', color: 'rgb(150 140 255 / 0.12)', dx: '-4vw', dy: '-6vh', t: 36, delay: -14 },
  ],
  surge: [
    { x: '50%', y: '58%', size: '70vmax', color: 'rgb(0 117 217 / 0.62)', dx: '0vw', dy: '-6vh', t: 18 },
    { x: '22%', y: '30%', size: '40vmax', color: 'rgb(127 216 255 / 0.22)', dx: '8vw', dy: '6vh', t: 22, delay: -5 },
    { x: '82%', y: '78%', size: '44vmax', color: 'rgb(84 82 230 / 0.3)', dx: '-8vw', dy: '-4vh', t: 26, delay: -9 },
  ],
}

interface LightFieldProps {
  variant: LightFieldVariant
  /** La luce scorre lentamente (solo dove serve: costa composizione anche fuori schermo). */
  flow?: boolean
}

/** Luce liquida dietro le lastre di vetro: decorativa, sotto il contenuto della sezione (che deve essere `isolate`). */
export function LightField({ variant, flow = false }: LightFieldProps) {
  return (
    <div className="light-field" data-flow={flow || undefined} aria-hidden="true">
      {FIELDS[variant].map((glow) => (
        <span
          key={`${glow.x}-${glow.y}`}
          style={cssVars({
            '--x': glow.x,
            '--y': glow.y,
            '--size': glow.size,
            '--c': glow.color,
            '--dx': glow.dx,
            '--dy': glow.dy,
            '--t': glow.t ? `${glow.t}s` : undefined,
            '--delay': glow.delay ? `${glow.delay}s` : undefined,
          })}
        />
      ))}
    </div>
  )
}
